import { makeImmutable } from "../../core/makeImmutable";
import { CustomParameterMultiSet, InternalParameterMultiSet, ParameterMultiSet, ScheduleParameterMultiSet, } from "../ParameterMultiSet";
import { CustomParameterSet } from "./CustomParameterSet";
import { InternalParameterSet } from "./InternalParameterSet";
import { ScheduleParameterSet } from "./ScheduleParameterSet";
/**
 * A single combination of different parameters.
 *
 * For example: "size: 30cm, dough: thick, fulfillmentMethod: delivery"
 */
export class ParameterSet {
    constructor(params) {
        var _a, _b, _c;
        this.internal = (_a = params.internal) !== null && _a !== void 0 ? _a : InternalParameterSet.empty();
        this.custom = (_b = params.custom) !== null && _b !== void 0 ? _b : CustomParameterSet.empty();
        this.schedule = (_c = params.schedule) !== null && _c !== void 0 ? _c : ScheduleParameterSet.empty();
        makeImmutable(this);
    }
    get customParameterTypes() {
        return this.custom.parameterTypes;
    }
    get isEmpty() {
        return (this.custom.isEmpty && this.internal.isEmpty && this.schedule.isEmpty);
    }
    static empty() {
        return new ParameterSet({});
    }
    unify(other) {
        const internal = this.internal.unify(other.internal);
        const custom = this.custom.unify(other.custom);
        const schedule = this.schedule.unify(other.schedule);
        const matchingParameters = new ParameterMultiSet({
            internal: internal.matchingParameters,
            custom: custom.matchingParameters,
            schedule: schedule.matchingParameters,
        });
        if (internal.type === "success" &&
            custom.type === "success" &&
            schedule.type === "success") {
            return {
                type: "success",
                matchingParameters,
                value: new ParameterSet({
                    internal: internal.value,
                    custom: custom.value,
                    schedule: schedule.value,
                }),
            };
        }
        return {
            type: "failure",
            matchingParameters,
            incompatibleParameters: new ParameterMultiSet({
                internal: internal.type === "failure"
                    ? internal.incompatibleParameters
                    : InternalParameterMultiSet.empty(),
                custom: custom.type === "failure"
                    ? custom.incompatibleParameters
                    : CustomParameterMultiSet.empty(),
                schedule: schedule.type === "failure"
                    ? schedule.incompatibleParameters
                    : ScheduleParameterMultiSet.empty(),
            }),
        };
    }
    /**
     * Unites two ParameterSets
     *
     * Fails if predicates from one set conflict with predicates from another set
     */
    union(other) {
        const internal = this.internal.union(other.internal);
        if (internal === null) {
            return null;
        }
        const custom = this.custom.union(other.custom);
        if (custom === null) {
            return null;
        }
        const schedule = this.schedule.union(other.schedule);
        if (schedule === null) {
            return null;
        }
        return new ParameterSet({ internal, custom, schedule });
    }
    toDebugJSON() {
        const result = {};
        Object.assign(result, this.internal);
        for (const [parameter, choiceId] of this.custom.choices) {
            result["custom:" + parameter.id.value] = choiceId;
        }
        for (const [schedule, available] of this.schedule.values) {
            result["schedule:" + schedule.id.value] = available;
        }
        return result;
    }
}
